export const DYNAMIC_YIELD_COOKIEBOT_CONSENT_CATEGORY = 'marketing'

const COOKIEBOT_COOKIE_NAME = 'CookieConsent'
const DYNAMIC_YIELD_ID_COOKIE_NAME = '_dyid'
const DYNAMIC_YIELD_SERVER_ID_COOKIE_NAME = '_dyid_server'
const DYNAMIC_YIELD_SERVER_COOKIE_SYNC_ENDPOINT = '/api/dynamic-yield/sync-dyid'

let dynamicYieldServerCookieRetryTimeout: number | undefined
let dynamicYieldServerCookieSyncKey: string | null = null
let dynamicYieldServerCookieSyncInFlight = false

const readConsentCategoryFromValue = (
  cookieValue: string,
  consentCategory: string
): boolean | null => {
  try {
    const parsedCookieValue = JSON.parse(cookieValue)
    const parsedConsentValue = parsedCookieValue?.[consentCategory]

    if (typeof parsedConsentValue === 'boolean') {
      return parsedConsentValue
    }
  } catch {
    // CookieBot di norma salva JSON, ma manteniamo anche una lettura testuale
    // per tollerare formati legacy/semplificati durante i test manuali.
  }

  const consentPattern = new RegExp(
    `"?${consentCategory}"?\\s*:\\s*(true|false)`
  )

  const consentMatch = cookieValue.match(consentPattern)

  if (consentMatch?.[1] === 'true') {
    return true
  }

  if (consentMatch?.[1] === 'false') {
    return false
  }

  return null
}

export const getCookiebotConsentFromCookieString = (
  cookieString: string,
  consentCategory = DYNAMIC_YIELD_COOKIEBOT_CONSENT_CATEGORY
): boolean | null => {
  const cookiebotCookie = cookieString
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${COOKIEBOT_COOKIE_NAME}=`))

  if (!cookiebotCookie) {
    return null
  }

  const cookieValue = cookiebotCookie.slice(COOKIEBOT_COOKIE_NAME.length + 1)
  let decodedCookieValue = cookieValue

  try {
    decodedCookieValue = decodeURIComponent(cookieValue)
  } catch {
    decodedCookieValue = cookieValue
  }

  return readConsentCategoryFromValue(decodedCookieValue, consentCategory)
}

export const getDynamicYieldConsent = () => {
  if (typeof window === 'undefined') {
    return false
  }

  const cookiebotConsent =
    window.Cookiebot?.consent?.[DYNAMIC_YIELD_COOKIEBOT_CONSENT_CATEGORY]

  if (typeof cookiebotConsent === 'boolean') {
    return cookiebotConsent
  }

  // CookieBot arriva da GTM e puo' non essere ancora disponibile al primo render.
  // Se l'utente ha gia' scelto in una visita precedente, il cookie CookieConsent
  // ci permette comunque di inizializzare DY con il valore salvato.
  return getCookiebotConsentFromCookieString(document.cookie) ?? false
}

const getCookieValueFromCookieString = (
  cookieString: string,
  cookieName: string
) => {
  const cookie = cookieString
    .split(';')
    .map((cookiePart) => cookiePart.trim())
    .find((cookiePart) => cookiePart.startsWith(`${cookieName}=`))

  return cookie?.slice(cookieName.length + 1) ?? ''
}

const callDynamicYieldServerCookieSync = (
  accepted: boolean,
  syncKey: string
) => {
  if (typeof window === 'undefined') {
    return
  }

  if (
    dynamicYieldServerCookieSyncInFlight ||
    dynamicYieldServerCookieSyncKey === syncKey
  ) {
    return
  }

  dynamicYieldServerCookieSyncKey = syncKey
  dynamicYieldServerCookieSyncInFlight = true

  const operation = accepted ? 'sync' : 'clear'

  fetch(`${DYNAMIC_YIELD_SERVER_COOKIE_SYNC_ENDPOINT}?operation=${operation}`, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
  })
    .catch(() => {
      // Il sync del cookie server-side e' un'integrazione di supporto: non deve
      // bloccare la pagina o le campagne DY in caso di errore di rete.
      dynamicYieldServerCookieSyncKey = null
    })
    .finally(() => {
      dynamicYieldServerCookieSyncInFlight = false
    })
}

export const syncDynamicYieldServerCookie = (accepted: boolean) => {
  if (typeof window === 'undefined') {
    return false
  }

  if (!accepted) {
    if (dynamicYieldServerCookieRetryTimeout) {
      window.clearTimeout(dynamicYieldServerCookieRetryTimeout)
      dynamicYieldServerCookieRetryTimeout = undefined
    }

    const dynamicYieldServerId = getCookieValueFromCookieString(
      document.cookie,
      DYNAMIC_YIELD_SERVER_ID_COOKIE_NAME
    )

    if (!dynamicYieldServerId) {
      return true
    }

    callDynamicYieldServerCookieSync(false, 'clear')

    return true
  }

  const dynamicYieldId = getCookieValueFromCookieString(
    document.cookie,
    DYNAMIC_YIELD_ID_COOKIE_NAME
  )

  if (!dynamicYieldId) {
    return false
  }

  const dynamicYieldServerId = getCookieValueFromCookieString(
    document.cookie,
    DYNAMIC_YIELD_SERVER_ID_COOKIE_NAME
  )

  if (dynamicYieldServerId === dynamicYieldId) {
    if (dynamicYieldServerCookieRetryTimeout) {
      window.clearTimeout(dynamicYieldServerCookieRetryTimeout)
      dynamicYieldServerCookieRetryTimeout = undefined
    }

    return true
  }

  if (dynamicYieldServerCookieRetryTimeout) {
    window.clearTimeout(dynamicYieldServerCookieRetryTimeout)
    dynamicYieldServerCookieRetryTimeout = undefined
  }

  callDynamicYieldServerCookieSync(true, `sync:${dynamicYieldId}`)

  return true
}

export const scheduleDynamicYieldServerCookieSync = (
  accepted: boolean,
  remainingAttempts = 6
) => {
  if (typeof window === 'undefined') {
    return
  }

  const hasSyncedServerCookie = syncDynamicYieldServerCookie(accepted)

  if (hasSyncedServerCookie) {
    return
  }

  if (!accepted) {
    return
  }

  if (remainingAttempts <= 0) {
    return
  }

  if (dynamicYieldServerCookieRetryTimeout) {
    return
  }

  dynamicYieldServerCookieRetryTimeout = window.setTimeout(() => {
    dynamicYieldServerCookieRetryTimeout = undefined
    scheduleDynamicYieldServerCookieSync(true, remainingAttempts - 1)
  }, 500)
}

export const syncDynamicYieldConsent = () => {
  const accepted = getDynamicYieldConsent()

  window.DY = window.DY ?? {}
  window.DY.userActiveConsent = { accepted }
  window.DYO?.ActiveConsent?.updateConsentAcceptedStatus?.(accepted)
  scheduleDynamicYieldServerCookieSync(accepted)

  return accepted
}

export const getDynamicYieldInlineConsentScript = () => `
  (function () {
    var consentCategory = '${DYNAMIC_YIELD_COOKIEBOT_CONSENT_CATEGORY}';

    function readCookiebotConsentCookie() {
      var cookieName = '${COOKIEBOT_COOKIE_NAME}=';
      var cookies = document.cookie ? document.cookie.split(';') : [];

      function readConsentCategoryFromValue(cookieValue) {
        try {
          var parsedCookieValue = JSON.parse(cookieValue);
          var parsedConsentValue = parsedCookieValue && parsedCookieValue[consentCategory];

          if (typeof parsedConsentValue === 'boolean') {
            return parsedConsentValue;
          }
        } catch (error) {
          // CookieBot di norma salva JSON, ma manteniamo anche una lettura
          // testuale per tollerare formati legacy/semplificati nei test.
        }

        var consentPattern = new RegExp('"?' + consentCategory + '"?\\\\s*:\\\\s*(true|false)');
        var consentMatch = cookieValue.match(consentPattern);

        if (consentMatch && consentMatch[1] === 'true') {
          return true;
        }

        if (consentMatch && consentMatch[1] === 'false') {
          return false;
        }

        return null;
      }

      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i].trim();

        if (cookie.indexOf(cookieName) !== 0) {
          continue;
        }

        var cookieValue = cookie.substring(cookieName.length);

        try {
          cookieValue = decodeURIComponent(cookieValue);
        } catch (error) {
          cookieValue = cookie.substring(cookieName.length);
        }

        return readConsentCategoryFromValue(cookieValue);
      }

      return null;
    }

    function readCookiebotConsent() {
      if (
        window.Cookiebot &&
        window.Cookiebot.consent &&
        typeof window.Cookiebot.consent[consentCategory] === 'boolean'
      ) {
        return window.Cookiebot.consent[consentCategory];
      }

      // Se GTM/CookieBot non sono ancora pronti, usiamo il cookie salvato.
      // In assenza di scelta partiamo da false: DY carica gli script, ma non
      // considera l'utente consenziente.
      return readCookiebotConsentCookie() || false;
    }

    window.DY.userActiveConsent = { accepted: readCookiebotConsent() };
  })();
`
